import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Info, ChevronDown, ChevronUp, Sparkles, Zap, Shield, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/ModernNoticeBox-fa.json";
import en from "./locales/ModernNoticeBox-en.json";
import ar from "./locales/ModernNoticeBox-ar.json";
import tr from "./locales/ModernNoticeBox-tr.json";

// کامپوننت ذرات شناور پس‌زمینه
const FloatingParticles: React.FC<{ isActive: boolean }> = ({ isActive }) => {
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {Array.from({ length: 12 }).map((_, i) => (
                <motion.div
                    key={i}
                    className="absolute w-1 h-1 rounded-full"
                    style={{
                        background: "linear-gradient(45deg, #0038A8, #0072CE)",
                        left: Math.random() * 100 + "%",
                        top: Math.random() * 100 + "%",
                    }}
                    animate={{
                        y: isActive ? ["-20px", "20px", "-20px"] : ["0px", "-10px", "0px"],
                        x: ["-5px", "5px", "-5px"],
                        opacity: isActive ? [0.3, 0.8, 0.3] : [0.1, 0.4, 0.1],
                        scale: isActive ? [1, 1.5, 1] : [0.5, 1, 0.5],
                    }}
                    transition={{
                        duration: 3 + Math.random() * 2,
                        repeat: Infinity,
                        delay: Math.random() * 2,
                        ease: "easeInOut",
                    }}
                />
            ))}
        </div>
    );
};

// کامپوننت افکت درخشش
const ShineEffect: React.FC<{ isActive: boolean }> = ({ isActive }) => {
    return (
        <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12"
            initial={{ x: "-100%" }}
            animate={{ x: isActive ? "100%" : "-100%" }}
            transition={{ duration: 1.2, ease: "easeInOut" }}
        />
    );
};

// کامپوننت ریپل افکت
const RippleEffect: React.FC<{ x: number; y: number; isVisible: boolean }> = ({ x, y, isVisible }) => {
    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className="absolute pointer-events-none"
                    style={{ left: x - 30, top: y - 30 }}
                    initial={{ scale: 0, opacity: 0.6 }}
                    animate={{ scale: 3, opacity: 0 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-400/40 to-blue-600/40 border border-blue-400/60" />
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const ModernNoticeBox: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "ModernNoticeBox";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [isExpanded, setIsExpanded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [ripple, setRipple] = useState({ x: 0, y: 0, isVisible: false });
    const containerRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(containerRef, { once: true, margin: "-50px" });

    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";

    const handleClick = (e: React.MouseEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        setRipple({ x, y, isVisible: true });
        setIsExpanded(!isExpanded);

        setTimeout(() => {
            setRipple((prev) => ({ ...prev, isVisible: false }));
        }, 800);
    };

    const featureIcons = [<Smartphone className="w-4 h-4" key="fi0" />, <Zap className="w-4 h-4" key="fi1" />, <Shield className="w-4 h-4" key="fi2" />, <Sparkles className="w-4 h-4" key="fi3" />];

    const features = Array.from({ length: 4 }, (_, i) => {
        const feat = t(`features.${i}`, { ns: "ModernNoticeBox", returnObjects: true, defaultValue: null }) as any;
        if (feat && feat.text) {
            return {
                ...feat,
                icon: featureIcons[i],
            };
        }
        return null;
    }).filter(Boolean) as Array<{ text: string; icon: React.ReactNode }>;

    return (
        <motion.div
            ref={containerRef}
            className="w-full max-w-5xl mx-auto md:px-4"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
            dir={dir}
        >
            <motion.div
                className="relative overflow-hidden rounded-3xl cursor-pointer group"
                onClick={handleClick}
                onHoverStart={() => setIsHovered(true)}
                onHoverEnd={() => setIsHovered(false)}
                whileHover={{ scale: 1.02, y: -6, boxShadow: "0 25px 50px -12px rgba(0, 114, 206, 0.25)" }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                style={{ perspective: "1000px" }}
            >
                <motion.div
                    className="absolute inset-0 rounded-3xl"
                    style={{
                        background: "linear-gradient(135deg, #0038A8 0%, #0072CE 50%, #EBF5FF 100%)",
                        backgroundSize: "200% 200%",
                    }}
                    animate={{ backgroundPosition: isHovered ? ["0% 50%", "100% 50%", "0% 50%"] : "0% 50%" }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                />

                <div className="relative m-[2px] bg-white/95 backdrop-blur-xl rounded-3xl overflow-hidden shadow-2xl border border-white/20">
                    <FloatingParticles isActive={isHovered || isExpanded} />

                    <div className="absolute inset-0 overflow-hidden rounded-3xl">
                        <ShineEffect isActive={isHovered} />
                    </div>

                    <div className="relative z-10 p-5 md:p-8">
                        <div className="flex items-start gap-6" dir={dir}>
                            <motion.div className="relative flex-shrink-0 order-2" whileHover={{ scale: 1.1, rotate: 5 }} whileTap={{ scale: 0.95 }}>
                                <motion.div
                                    className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/30 to-blue-600/30 blur-lg"
                                    animate={{
                                        scale: isExpanded ? [1, 1.3, 1] : 1,
                                        opacity: isExpanded ? [0.5, 0.9, 0.5] : 0.6,
                                    }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                />
                            </motion.div>

                            <div className="flex-1">
                                <div className="flex flex-row items-center mb-4 md:mb-6">
                                    <motion.div
                                        className="relative w-16 h-16 rounded-2xl backdrop-blur-sm border border-white/40 flex items-center justify-center"
                                        style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)" }}
                                        transition={{ duration: 0.6, repeat: isExpanded ? Infinity : 0, repeatType: "reverse" }}
                                    >
                                        <motion.div className="text-[#0072CE]" animate={{ filter: isExpanded ? "drop-shadow(0 0 12px rgba(0,114,206,0.6))" : "none" }}>
                                            <Info className="w-8 h-8" />
                                        </motion.div>
                                    </motion.div>
                                    <motion.h3
                                        className={`text-2xl ${isRTL ? "mr-3 md:mr-5" : "ml-3 md:ml-5"} font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-[#0038A8] to-[#0072CE]`}
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ type: "spring", delay: 0.2 }}
                                    >
                                        {t("title", { ns: "ModernNoticeBox" })}
                                    </motion.h3>
                                </div>

                                <motion.p className="text-gray-700 mb-4 leading-relaxed text-justify text-lg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                                    {t("mainText", { ns: "ModernNoticeBox" })}
                                </motion.p>

                                <AnimatePresence>
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0, y: -20 }}
                                            animate={{ height: "auto", opacity: 1, y: 0 }}
                                            exit={{ height: 0, opacity: 0, y: -20 }}
                                            transition={{ duration: 0.4, ease: "easeOut" }}
                                            className="overflow-hidden"
                                        >
                                            <div
                                                className="p-4 md:p-6 rounded-2xl mb-6 border border-blue-200/50 relative overflow-hidden"
                                                style={{ background: "linear-gradient(135deg, rgba(235,245,255,0.4) 0%, rgba(0,114,206,0.1) 100%)" }}
                                            >
                                                <motion.div
                                                    className="absolute inset-0 bg-gradient-to-r from-blue-100/20 via-blue-200/30 to-blue-100/20"
                                                    animate={{ x: ["-100%", "100%", "-100%"] }}
                                                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                                                />

                                                <div className="relative z-10">
                                                    <motion.h4
                                                        className="text-lg font-semibold text-[#0038A8] mb-4 flex items-center gap-2"
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: 0.2 }}
                                                    >
                                                        {t("detailsTitle", { ns: "ModernNoticeBox" })}
                                                        <Sparkles className="w-5 h-5" />
                                                    </motion.h4>

                                                    <div className="flex flex-col gap-4">
                                                        {features.map((feature, index) => (
                                                            <motion.div
                                                                key={index}
                                                                className="flex items-center gap-3 justify-end p-3 rounded-xl bg-white/60 backdrop-blur-sm border border-white/40 hover:bg-white/80 transition-all duration-300"
                                                                initial={{ x: 30, opacity: 0 }}
                                                                animate={{ x: 0, opacity: 1 }}
                                                                transition={{ delay: 0.1 * index + 0.3 }}
                                                                whileHover={{ scale: 1.02, boxShadow: "0 8px 25px -8px rgba(0,114,206,0.3)" }}
                                                            >
                                                                <motion.div
                                                                    className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#0038A8] to-[#0072CE] flex items-center justify-center text-white shadow-lg"
                                                                    whileHover={{ rotate: 360 }}
                                                                    transition={{ duration: 0.6 }}
                                                                >
                                                                    {feature.icon}
                                                                </motion.div>
                                                                <span className={`flex-1 text-start text-gray-700`}>{feature.text}</span>
                                                            </motion.div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <div className="flex">
                                    <motion.button
                                        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0038A8] to-[#0072CE] text-white font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                                        whileHover={{ scale: 1.05, boxShadow: "0 12px 30px -8px rgba(0,114,206,0.4)" }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsExpanded(!isExpanded);
                                        }}
                                    >
                                        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
                                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </motion.div>
                                        <span>{isExpanded ? t("buttons.collapse", { ns: "ModernNoticeBox" }) : t("buttons.expand", { ns: "ModernNoticeBox" })}</span>
                                    </motion.button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <motion.div
                        className="absolute inset-0 rounded-3xl pointer-events-none"
                        style={{
                            background: "linear-gradient(45deg, transparent, rgba(0,56,168,0.4), transparent, rgba(0,114,206,0.4), transparent)",
                            backgroundSize: "300% 300%",
                            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                            maskComposite: "xor",
                            padding: "2px",
                        }}
                        animate={{ backgroundPosition: isHovered ? ["0% 50%", "100% 50%", "0% 50%"] : "0% 50%" }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    />

                    <RippleEffect x={ripple.x} y={ripple.y} isVisible={ripple.isVisible} />
                </div>
            </motion.div>
        </motion.div>
    );
};

export default ModernNoticeBox;
